import React, { useState, useEffect, useRef } from "react";
import "./optimizedImage.scss";

const OptimizedImage = ({
  src,
  alt = "",
  className = "",
  style = {},
  placeholder = null,
  fallback = null,
  lazy = true,
  onLoad,
  onError,
  ...props
}) => {
  const [imageSrc, setImageSrc] = useState(placeholder || "");
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);
  const observerRef = useRef(null);

  useEffect(() => {
    const loadImage = () => {
      if (!src) return;

      setIsLoading(true);
      setHasError(false);

      // Check if image is already cached
      const img = new Image();

      img.onload = () => {
        setImageSrc(src);
        setIsLoading(false);
        if (onLoad) onLoad();
      };

      img.onerror = () => {
        setHasError(true);
        setImageSrc(fallback || placeholder || "");
        setIsLoading(false);
        if (onError) onError();
      };

      img.src = src;
    };

    if (!src) {
      setImageSrc(fallback || placeholder || "");
      setIsLoading(false);
      return;
    }

    // If lazy loading is disabled, load immediately
    if (!lazy) {
      loadImage();
      return;
    }

    // Set up Intersection Observer for lazy loading
    if (imgRef.current && "IntersectionObserver" in window) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              loadImage();
              if (observerRef.current) {
                observerRef.current.disconnect();
              }
            }
          });
        },
        {
          rootMargin: "50px", // Start loading 50px before image enters viewport
        }
      );

      observerRef.current.observe(imgRef.current);
    } else {
      // Fallback for browsers without Intersection Observer
      loadImage();
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [src, lazy, fallback, placeholder, onLoad, onError]);

  return (
    <div
      className={`optimized-image-wrapper ${className} ${isLoading ? "loading" : ""} ${hasError ? "error" : ""}`}
      style={style}
    >
      <img
        ref={imgRef}
        src={imageSrc}
        alt={alt}
        className={`optimized-image ${isLoading ? "image-loading" : "image-loaded"}`}
        loading={lazy ? "lazy" : "eager"}
        decoding="async"
        {...props}
      />
      {isLoading && placeholder && (
        <div className="image-placeholder">
          <img src={placeholder} alt="" aria-hidden="true" />
        </div>
      )}
    </div>
  );
};

export default OptimizedImage;
